import React, { createContext, useContext, useState, useEffect } from 'react';

const DevModeContext = createContext();

export const useDevMode = () => {
  const context = useContext(DevModeContext);
  if (!context) {
    throw new Error('useDevMode must be used within DevModeProvider');
  }
  return context;
};

// Dummy data for development
export const dummyData = {
  users: [
    { id: 'user1', field_12: { full: 'John Doe' }, field_12_raw: { full: 'John Doe' }, field_10_raw: 'john@example.com' },
    { id: 'user2', field_12: { full: 'Jane Smith' }, field_12_raw: { full: 'Jane Smith' }, field_10_raw: 'jane@example.com' },
    { id: 'user3', field_12: { full: 'Bob Johnson' }, field_12_raw: { full: 'Bob Johnson' }, field_10_raw: 'bob@example.com' },
    { id: 'user4', field_12: { full: 'Alice Williams' }, field_12_raw: { full: 'Alice Williams' }, field_10_raw: 'alice@example.com' },
    { id: 'user5', field_12: { full: 'Charlie Brown' }, field_12_raw: { full: 'Charlie Brown' }, field_10_raw: 'charlie@example.com' },
  ],
  
  projects: [
    { id: 'proj1', field_2_raw: 'GE-2024-001', field_5_raw: 'Airport Terminal Modernization', field_117_raw: 'United States' },
    { id: 'proj2', field_2_raw: 'GE-2024-002', field_5_raw: 'Runway Extension Project', field_117_raw: 'Canada' },
    { id: 'proj3', field_2_raw: 'GE-2024-003', field_5_raw: 'Security System Upgrade', field_117_raw: 'United Kingdom' },
    { id: 'proj4', field_2_raw: 'GE-2024-004', field_5_raw: 'Baggage Handling System', field_117_raw: 'Germany' },
    { id: 'proj5', field_2_raw: 'GE-2024-005', field_5_raw: 'Air Traffic Control Tower', field_117_raw: 'France' },
  ],
  
  tasks: [
    {
      id: 'task1',
      field_43_raw: 'Design Review Meeting',
      field_44_raw: 'Review architectural plans with stakeholders',
      field_45_raw: 40,
      field_46: '10/30/2025',
      field_46_raw: { date: '10/30/2025' },
      field_48_raw: [{ id: 'proj1', identifier: 'GE-2024-001', field_117_raw: 'United States' }],
      field_50_raw: false,
      field_61_raw: [{ id: 'user1', field_12: { full: 'John Doe' } }],
      field_183_raw: 15,
      field_184_raw: 37.5
    },
    {
      id: 'task2',
      field_43_raw: 'Budget Planning',
      field_44_raw: 'Prepare detailed budget breakdown for Q1',
      field_45_raw: 20,
      field_46: '10/25/2025',
      field_46_raw: { date: '10/25/2025' },
      field_48_raw: [{ id: 'proj2', identifier: 'GE-2024-002', field_117_raw: 'Canada' }],
      field_50_raw: false,
      field_61_raw: [{ id: 'user1', field_12: { full: 'John Doe' } }, { id: 'user2', field_12: { full: 'Jane Smith' } }],
      field_183_raw: 8,
      field_184_raw: 40
    },
    {
      id: 'task3',
      field_43_raw: 'Vendor Selection',
      field_44_raw: 'Evaluate and select primary contractor',
      field_45_raw: 30,
      field_46: '11/15/2025',
      field_46_raw: { date: '11/15/2025' },
      field_48_raw: [{ id: 'proj3', identifier: 'GE-2024-003', field_117_raw: 'United Kingdom' }],
      field_50_raw: false,
      field_61_raw: [{ id: 'user1', field_12: { full: 'John Doe' } }],
      field_183_raw: 25,
      field_184_raw: 83
    },
    {
      id: 'task4',
      field_43_raw: 'Site Inspection',
      field_44_raw: 'Conduct comprehensive site safety inspection',
      field_45_raw: 8,
      field_46: '10/22/2025',
      field_46_raw: { date: '10/22/2025' },
      field_48_raw: [{ id: 'proj4', identifier: 'GE-2024-004', field_117_raw: 'Germany' }],
      field_50_raw: true,
      field_61_raw: [{ id: 'user1', field_12: { full: 'John Doe' } }],
      field_183_raw: 8,
      field_184_raw: 100
    },
  ],
  
  hours: [
    { id: 'hour1', field_111_raw: [{ id: 'task1' }], field_112_raw: 5, field_113: '10/21/2025', field_114_raw: 'Initial planning session' },
    { id: 'hour2', field_111_raw: [{ id: 'task1' }], field_112_raw: 4, field_113: '10/20/2025', field_114_raw: 'Requirements gathering' },
    { id: 'hour3', field_111_raw: [{ id: 'task2' }], field_112_raw: 3, field_113: '10/21/2025', field_114_raw: 'Cost analysis' },
  ],
  
  // Extended project data for admin views
  allProjects: [
    {
      id: 'proj1',
      field_2_raw: 'GE-2024-001',
      field_5_raw: 'Airport Terminal Modernization',
      field_117_raw: 'United States',
      status: 'Ongoing',
      stage: 'Execution',
      value: 2500000,
      probability: 95, // percentage
      startDate: '08/15/2024',
      endDate: '12/31/2025',
      deadline: '12/31/2025',
      progress: 45,
      projectManager: 'John Doe',
      team: ['John Doe', 'Jane Smith', 'Bob Johnson'],
      hoursSpent: 850,
      hoursEstimated: 2000,
      budget: 2500000,
      spent: 1125000,
      health: 'on-track',
      waitingFinalPayment: false,
      lastUpdate: {
        date: '10/20/2025',
        time: '14:30',
        text: 'Phase 2 design review completed. Moving to procurement.',
        author: 'John Doe'
      },
      updateHistory: [
        { date: '10/20/2025', time: '14:30', text: 'Phase 2 design review completed. Moving to procurement.', author: 'John Doe' },
        { date: '10/15/2025', time: '09:15', text: 'Stakeholder meeting held. Approved budget increase.', author: 'John Doe' },
        { date: '10/10/2025', time: '16:45', text: 'Completed structural assessment. Minor revisions needed.', author: 'Jane Smith' },
        { date: '10/05/2025', time: '11:20', text: 'Started Phase 2 design work.', author: 'John Doe' }
      ]
    },
    {
      id: 'proj2',
      field_2_raw: 'GE-2024-002',
      field_5_raw: 'Runway Extension Project',
      field_117_raw: 'Canada',
      status: 'Ongoing',
      stage: 'Planning',
      value: 4800000,
      probability: 90,
      startDate: '09/01/2024',
      endDate: '06/30/2026',
      deadline: '06/30/2026',
      progress: 22,
      projectManager: 'Jane Smith',
      team: ['Jane Smith', 'Alice Williams', 'Charlie Brown'],
      hoursSpent: 340,
      hoursEstimated: 3500,
      budget: 4800000,
      spent: 960000,
      health: 'on-track',
      waitingFinalPayment: false,
      lastUpdate: {
        date: '10/21/2025',
        time: '10:00',
        text: 'Environmental impact study submitted to authorities.',
        author: 'Jane Smith'
      },
      updateHistory: [
        { date: '10/21/2025', time: '10:00', text: 'Environmental impact study submitted to authorities.', author: 'Jane Smith' },
        { date: '10/18/2025', time: '13:45', text: 'Finalized preliminary design drawings.', author: 'Alice Williams' },
        { date: '10/12/2025', time: '15:30', text: 'Site survey completed. No major obstacles found.', author: 'Charlie Brown' }
      ]
    },
    {
      id: 'proj3',
      field_2_raw: 'GE-2024-003',
      field_5_raw: 'Security System Upgrade',
      field_117_raw: 'United Kingdom',
      status: 'Ongoing',
      stage: 'Execution',
      value: 1200000,
      probability: 100,
      startDate: '07/01/2024',
      endDate: '11/15/2025',
      deadline: '11/15/2025',
      progress: 78,
      projectManager: 'Bob Johnson',
      team: ['Bob Johnson', 'John Doe'],
      hoursSpent: 1560,
      hoursEstimated: 2000,
      budget: 1200000,
      spent: 936000,
      health: 'at-risk',
      waitingFinalPayment: true,
      lastUpdate: {
        date: '10/19/2025',
        time: '16:20',
        text: 'Installation phase 3 delayed due to equipment shortage.',
        author: 'Bob Johnson'
      },
      updateHistory: [
        { date: '10/19/2025', time: '16:20', text: 'Installation phase 3 delayed due to equipment shortage.', author: 'Bob Johnson' },
        { date: '10/14/2025', time: '11:00', text: 'Phase 2 testing completed successfully.', author: 'John Doe' },
        { date: '10/08/2025', time: '14:15', text: 'Installed 75% of security cameras.', author: 'Bob Johnson' }
      ]
    },
    {
      id: 'proj4',
      field_2_raw: 'GE-2024-004',
      field_5_raw: 'Baggage Handling System',
      field_117_raw: 'Germany',
      status: 'Bid',
      stage: 'Bidding',
      value: 3200000,
      probability: 60,
      startDate: '11/01/2024',
      endDate: '08/30/2026',
      deadline: '11/05/2024',
      progress: 5,
      projectManager: 'Alice Williams',
      team: ['Alice Williams', 'Charlie Brown'],
      hoursSpent: 45,
      hoursEstimated: 2800,
      budget: 3200000,
      spent: 0,
      health: 'on-track',
      waitingFinalPayment: false,
      lastUpdate: {
        date: '10/21/2025',
        time: '09:00',
        text: 'Final proposal review meeting scheduled for 10/25.',
        author: 'Alice Williams'
      },
      updateHistory: [
        { date: '10/21/2025', time: '09:00', text: 'Final proposal review meeting scheduled for 10/25.', author: 'Alice Williams' },
        { date: '10/17/2025', time: '14:30', text: 'Technical specifications completed.', author: 'Charlie Brown' },
        { date: '10/12/2025', time: '10:45', text: 'Pricing analysis completed. Competitive bid prepared.', author: 'Alice Williams' }
      ]
    },
    {
      id: 'proj5',
      field_2_raw: 'GE-2024-005',
      field_5_raw: 'Air Traffic Control Tower',
      field_117_raw: 'France',
      status: 'Lead',
      stage: 'Lead',
      value: 5500000,
      probability: 40,
      startDate: '01/15/2025',
      endDate: '12/31/2026',
      deadline: '01/15/2025',
      progress: 0,
      projectManager: 'Charlie Brown',
      team: ['Charlie Brown'],
      hoursSpent: 12,
      hoursEstimated: 4000,
      budget: 5500000,
      spent: 0,
      health: 'on-track',
      waitingFinalPayment: false,
      lastUpdate: {
        date: '10/20/2025',
        time: '11:15',
        text: 'Initial client meeting scheduled for next week.',
        author: 'Charlie Brown'
      },
      updateHistory: [
        { date: '10/20/2025', time: '11:15', text: 'Initial client meeting scheduled for next week.', author: 'Charlie Brown' },
        { date: '10/15/2025', time: '15:00', text: 'Preliminary requirements gathered from client.', author: 'Charlie Brown' }
      ]
    },
    {
      id: 'proj6',
      field_2_raw: 'GE-2023-087',
      field_5_raw: 'Maintenance Facility Construction',
      field_117_raw: 'United States',
      status: 'Ongoing',
      stage: 'Execution',
      value: 1800000,
      probability: 100,
      startDate: '03/01/2024',
      endDate: '10/25/2024',
      deadline: '10/25/2024',
      progress: 92,
      projectManager: 'Jane Smith',
      team: ['Jane Smith', 'Bob Johnson', 'Alice Williams'],
      hoursSpent: 2210,
      hoursEstimated: 2400,
      budget: 1800000,
      spent: 1656000,
      health: 'delayed',
      waitingFinalPayment: true,
      lastUpdate: {
        date: '10/21/2025',
        time: '17:00',
        text: 'Final inspections in progress. Completion expected by 10/28.',
        author: 'Jane Smith'
      },
      updateHistory: [
        { date: '10/21/2025', time: '17:00', text: 'Final inspections in progress. Completion expected by 10/28.', author: 'Jane Smith' },
        { date: '10/18/2025', time: '12:30', text: 'Punch list items being addressed.', author: 'Bob Johnson' },
        { date: '10/15/2025', time: '09:45', text: 'Construction phase completed. Starting closeout.', author: 'Jane Smith' }
      ]
    },
  ],
  
  // Monthly statistics for graphs (last 12 months)
  monthlyStats: [
    { month: 'Nov 2024', newLeads: 3, newProjects: 1, projectsWon: 2, projectsLost: 0 },
    { month: 'Dec 2024', newLeads: 2, newProjects: 2, projectsWon: 1, projectsLost: 1 },
    { month: 'Jan 2025', newLeads: 4, newProjects: 1, projectsWon: 1, projectsLost: 0 },
    { month: 'Feb 2025', newLeads: 5, newProjects: 0, projectsWon: 0, projectsLost: 1 },
    { month: 'Mar 2025', newLeads: 3, newProjects: 1, projectsWon: 2, projectsLost: 0 },
    { month: 'Apr 2025', newLeads: 6, newProjects: 2, projectsWon: 1, projectsLost: 1 },
    { month: 'May 2025', newLeads: 4, newProjects: 1, projectsWon: 1, projectsLost: 0 },
    { month: 'Jun 2025', newLeads: 7, newProjects: 3, projectsWon: 2, projectsLost: 1 },
    { month: 'Jul 2025', newLeads: 5, newProjects: 2, projectsWon: 1, projectsLost: 1 },
    { month: 'Aug 2025', newLeads: 8, newProjects: 1, projectsWon: 2, projectsLost: 0 },
    { month: 'Sep 2025', newLeads: 6, newProjects: 2, projectsWon: 1, projectsLost: 1 },
    { month: 'Oct 2025', newLeads: 4, newProjects: 1, projectsWon: 1, projectsLost: 0 },
  ],
  
  // Employee workload data
  employees: [
    {
      id: 'user1',
      name: 'John Doe',
      field_12: { full: 'John Doe' },
      email: 'john@velocityairport.com',
      role: 'Project Manager',
      activeProjects: 2,
      totalHours: 850 + 1560,
      hoursThisWeek: 35,
      utilization: 87.5, // percentage
      projectIds: ['proj1', 'proj3'],
      photo: null,
      yearlyHours: {
        2022: 1850,
        2023: 1920,
        2024: 1980,
        2025: 2410 // current year accumulated
      },
      weeklyHours: [
        { week: 'Week 35', hours: 40, projects: [{ name: 'GE-2024-001', hours: 22 }, { name: 'GE-2024-003', hours: 18 }] },
        { week: 'Week 36', hours: 38, projects: [{ name: 'GE-2024-001', hours: 20 }, { name: 'GE-2024-003', hours: 18 }] },
        { week: 'Week 37', hours: 42, projects: [{ name: 'GE-2024-001', hours: 24 }, { name: 'GE-2024-003', hours: 18 }] },
        { week: 'Week 38', hours: 37, projects: [{ name: 'GE-2024-001', hours: 19 }, { name: 'GE-2024-003', hours: 18 }] },
        { week: 'Week 39', hours: 38, projects: [{ name: 'GE-2024-001', hours: 20 }, { name: 'GE-2024-003', hours: 18 }] },
        { week: 'Week 40', hours: 35, projects: [{ name: 'GE-2024-001', hours: 18 }, { name: 'GE-2024-003', hours: 17 }] },
        { week: 'Week 41', hours: 42, projects: [{ name: 'GE-2024-001', hours: 22 }, { name: 'GE-2024-003', hours: 20 }] },
        { week: 'Week 42', hours: 35, projects: [{ name: 'GE-2024-001', hours: 19 }, { name: 'GE-2024-003', hours: 16 }] },
      ]
    },
    {
      id: 'user2',
      name: 'Jane Smith',
      field_12: { full: 'Jane Smith' },
      email: 'jane@velocityairport.com',
      role: 'Senior Engineer',
      activeProjects: 3,
      totalHours: 850 + 340 + 2210,
      hoursThisWeek: 42,
      utilization: 105, // overutilized
      projectIds: ['proj1', 'proj2', 'proj6'],
      photo: null,
      yearlyHours: {
        2022: 2100,
        2023: 2150,
        2024: 2200,
        2025: 3400
      },
      weeklyHours: [
        { week: 'Week 35', hours: 40, projects: [{ name: 'GE-2024-001', hours: 15 }, { name: 'GE-2024-002', hours: 10 }, { name: 'GE-2023-087', hours: 15 }] },
        { week: 'Week 36', hours: 42, projects: [{ name: 'GE-2024-001', hours: 16 }, { name: 'GE-2024-002', hours: 11 }, { name: 'GE-2023-087', hours: 15 }] },
        { week: 'Week 37', hours: 40, projects: [{ name: 'GE-2024-001', hours: 15 }, { name: 'GE-2024-002', hours: 10 }, { name: 'GE-2023-087', hours: 15 }] },
        { week: 'Week 38', hours: 41, projects: [{ name: 'GE-2024-001', hours: 15 }, { name: 'GE-2024-002', hours: 11 }, { name: 'GE-2023-087', hours: 15 }] },
        { week: 'Week 39', hours: 40, projects: [{ name: 'GE-2024-001', hours: 15 }, { name: 'GE-2024-002', hours: 10 }, { name: 'GE-2023-087', hours: 15 }] },
        { week: 'Week 40', hours: 42, projects: [{ name: 'GE-2024-001', hours: 16 }, { name: 'GE-2024-002', hours: 11 }, { name: 'GE-2023-087', hours: 15 }] },
        { week: 'Week 41', hours: 45, projects: [{ name: 'GE-2024-001', hours: 17 }, { name: 'GE-2024-002', hours: 12 }, { name: 'GE-2023-087', hours: 16 }] },
        { week: 'Week 42', hours: 42, projects: [{ name: 'GE-2024-001', hours: 16 }, { name: 'GE-2024-002', hours: 11 }, { name: 'GE-2023-087', hours: 15 }] },
      ]
    },
    {
      id: 'user3',
      name: 'Bob Johnson',
      field_12: { full: 'Bob Johnson' },
      email: 'bob@velocityairport.com',
      role: 'Lead Engineer',
      activeProjects: 3,
      totalHours: 850 + 1560 + 2210,
      hoursThisWeek: 38,
      utilization: 95,
      projectIds: ['proj1', 'proj3', 'proj6'],
      photo: null,
      yearlyHours: {
        2022: 1950,
        2023: 2000,
        2024: 2050,
        2025: 4620
      },
      weeklyHours: [
        { week: 'Week 39', hours: 39, projects: [{ name: 'GE-2024-001', hours: 12 }, { name: 'GE-2024-003', hours: 15 }, { name: 'GE-2023-087', hours: 12 }] },
        { week: 'Week 40', hours: 38, projects: [{ name: 'GE-2024-001', hours: 13 }, { name: 'GE-2024-003', hours: 14 }, { name: 'GE-2023-087', hours: 11 }] },
        { week: 'Week 41', hours: 40, projects: [{ name: 'GE-2024-001', hours: 14 }, { name: 'GE-2024-003', hours: 15 }, { name: 'GE-2023-087', hours: 11 }] },
        { week: 'Week 42', hours: 38, projects: [{ name: 'GE-2024-001', hours: 13 }, { name: 'GE-2024-003', hours: 14 }, { name: 'GE-2023-087', hours: 11 }] },
      ]
    },
    {
      id: 'user4',
      name: 'Alice Williams',
      field_12: { full: 'Alice Williams' },
      email: 'alice@velocityairport.com',
      role: 'Bid Manager',
      activeProjects: 2,
      totalHours: 340 + 45,
      hoursThisWeek: 28,
      utilization: 70,
      projectIds: ['proj2', 'proj4'],
      photo: null,
      yearlyHours: {
        2022: 1650,
        2023: 1700,
        2024: 1750,
        2025: 385
      },
      weeklyHours: [
        { week: 'Week 39', hours: 30, projects: [{ name: 'GE-2024-002', hours: 18 }, { name: 'GE-2024-004', hours: 12 }] },
        { week: 'Week 40', hours: 28, projects: [{ name: 'GE-2024-002', hours: 16 }, { name: 'GE-2024-004', hours: 12 }] },
        { week: 'Week 41', hours: 32, projects: [{ name: 'GE-2024-002', hours: 20 }, { name: 'GE-2024-004', hours: 12 }] },
        { week: 'Week 42', hours: 28, projects: [{ name: 'GE-2024-002', hours: 16 }, { name: 'GE-2024-004', hours: 12 }] },
      ]
    },
    {
      id: 'user5',
      name: 'Charlie Brown',
      field_12: { full: 'Charlie Brown' },
      email: 'charlie@velocityairport.com',
      role: 'Business Developer',
      activeProjects: 2,
      totalHours: 340 + 12,
      hoursThisWeek: 15,
      utilization: 37.5,
      projectIds: ['proj2', 'proj5'],
      photo: null,
      yearlyHours: {
        2022: 1400,
        2023: 1450,
        2024: 1500,
        2025: 352
      },
      weeklyHours: [
        { week: 'Week 39', hours: 18, projects: [{ name: 'GE-2024-002', hours: 12 }, { name: 'GE-2024-005', hours: 6 }] },
        { week: 'Week 40', hours: 15, projects: [{ name: 'GE-2024-002', hours: 10 }, { name: 'GE-2024-005', hours: 5 }] },
        { week: 'Week 41', hours: 20, projects: [{ name: 'GE-2024-002', hours: 14 }, { name: 'GE-2024-005', hours: 6 }] },
        { week: 'Week 42', hours: 15, projects: [{ name: 'GE-2024-002', hours: 10 }, { name: 'GE-2024-005', hours: 5 }] },
      ]
    },
  ]
};

export const DevModeProvider = ({ children }) => {
  const [devMode, setDevMode] = useState(() => {
    const saved = localStorage.getItem('devMode');
    return saved === 'true';
  });

  const [apiCallCount, setApiCallCount] = useState(() => {
    const saved = localStorage.getItem('apiCallCount');
    const savedTime = localStorage.getItem('apiCallCountTime');
    const now = new Date();
    const savedDate = savedTime ? new Date(savedTime) : now;
    
    // Reset counter if it's a new day
    if (savedDate.toDateString() !== now.toDateString()) {
      localStorage.setItem('apiCallCount', '0');
      localStorage.setItem('apiCallCountTime', now.toISOString());
      return 0;
    }
    
    return parseInt(saved || '0');
  });

  useEffect(() => {
    localStorage.setItem('devMode', devMode.toString());
  }, [devMode]);

  const toggleDevMode = () => {
    setDevMode(prev => !prev);
  };

  const incrementApiCallCount = () => {
    setApiCallCount(prev => {
      const newCount = prev + 1;
      localStorage.setItem('apiCallCount', newCount.toString());
      localStorage.setItem('apiCallCountTime', new Date().toISOString());
      return newCount;
    });
  };

  const resetApiCallCount = () => {
    setApiCallCount(0);
    localStorage.setItem('apiCallCount', '0');
    localStorage.setItem('apiCallCountTime', new Date().toISOString());
  };

  return (
    <DevModeContext.Provider value={{
      devMode,
      toggleDevMode,
      apiCallCount,
      incrementApiCallCount,
      resetApiCallCount,
      dummyData
    }}>
      {children}
    </DevModeContext.Provider>
  );
};
